import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import moment from "moment";
import {
  FiArrowDown,
  FiArrowLeft,
  FiArrowUp,
  FiInbox,
  FiSearch,
  FiX,
} from "react-icons/fi";
import { GoalScoreCard } from "./goalScoreCard";
import MoneyballDatePicker from "../../common/components/datePicker";
import goalScoreService from "../../../api/services/goalScoreService";
import { playSong } from "../../common/actions/common.actions";

export const GoalScore = ({ setIsLoading }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [goalScore, setGoalScore] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [sortDescending, setSortDescending] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [hasError, setHasError] = useState(false);

  const handleSongClick = () => dispatch(playSong());

  const getData = useCallback(
    (date) => {
      setIsLoading(true);
      setHasError(false);

      goalScoreService
        .goalScores({
          startDate: moment(date).format("YYYY-MM-DD"),
          endDate: moment(date).format("YYYY-MM-DD"),
        })
        .then((response) => response.json())
        .then((data) => {
          const groups = Array.isArray(data?._data) ? data._data : [];
          setGoalScore(groups.flatMap((item) => item?.team || []));
        })
        .catch((error) => {
          setGoalScore([]);
          setHasError(true);
          console.error("API request error:", error);
        })
        .finally(() => setIsLoading(false));
    },
    [setIsLoading]
  );

  useEffect(() => {
    getData(selectedDate);
  }, [getData, selectedDate]);

  const visibleScores = useMemo(() => {
    const searchTerm = searchInput.trim().toLowerCase();
    const filtered = searchTerm
      ? goalScore.filter((item) =>
          [
            item?.league,
            item?.homeTeam,
            item?.homeTeamMyan,
            item?.awayTeam,
            item?.awayTeamMyan,
          ].some((value) => String(value || "").toLowerCase().includes(searchTerm))
        )
      : goalScore;

    return filtered.slice().sort((a, b) => {
      const difference = new Date(a?.event) - new Date(b?.event);
      return sortDescending ? -difference : difference;
    });
  }, [goalScore, searchInput, sortDescending]);

  const clearSearch = () => {
    handleSongClick();
    setSearchInput("");
    setShowSearch(false);
  };

  return (
    <div className="relative min-h-screen w-full min-w-0 flex-1 self-stretch overflow-hidden bg-gradient_start font-myanmarSansPro text-white">
      <div className="pointer-events-none absolute -right-24 top-20 h-72 w-72 rounded-full bg-emerald-300/10 blur-3xl" />

      <header className="relative z-30 bg-header_green shadow-lg shadow-black/20">
        <div className="mx-auto flex min-h-[60px] w-full max-w-2xl items-center gap-2 px-4 py-2">
          <button
            type="button"
            onClick={() => {
              handleSongClick();
              navigate("/dashboard");
            }}
            aria-label="Back to dashboard"
            className="flex h-10 w-10 flex-none items-center justify-center rounded-lg border border-white/10 bg-white/10 transition hover:bg-white/20 active:scale-95"
          >
            <FiArrowLeft className="h-5 w-5" />
          </button>

          {showSearch ? (
            <div className="relative min-w-0 flex-1">
              <FiSearch className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/40" />
              <input
                type="search"
                autoFocus
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                placeholder="Search teams or leagues"
                className="h-10 w-full rounded-lg border border-white/10 bg-white/10 pl-9 pr-10 font-sans text-sm text-white outline-none placeholder:text-white/35 focus:border-emerald-200/35 focus:bg-white/15"
              />
              <button
                type="button"
                onClick={clearSearch}
                aria-label="Close search"
                className="absolute right-1 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-md text-white/50 hover:bg-white/10 hover:text-white"
              >
                <FiX className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <div className="ml-1 min-w-0 flex-1">
              <h1 className="truncate text-base font-semibold leading-[1.8]">
                {t("goalScore")}
              </h1>
              <p className="font-sans text-[10px] uppercase tracking-[0.14em] text-white/40">
                {visibleScores.length} {t("matches")}
              </p>
            </div>
          )}

          {!showSearch && (
            <div className="flex flex-none items-center gap-1">
              <MoneyballDatePicker
                selected={selectedDate}
                onChange={(date) => date && setSelectedDate(date)}
                ariaLabel="Choose score date"
              />
              <HeaderButton
                label={sortDescending ? "Sort oldest first" : "Sort newest first"}
                onClick={() => {
                  handleSongClick();
                  setSortDescending((value) => !value);
                }}
              >
                {sortDescending ? <FiArrowDown /> : <FiArrowUp />}
              </HeaderButton>
              <HeaderButton
                label="Search scores"
                onClick={() => {
                  handleSongClick();
                  setShowSearch(true);
                }}
              >
                <FiSearch />
              </HeaderButton>
            </div>
          )}
        </div>
      </header>

      <main className="relative z-10 mx-auto h-[calc(100vh-60px)] w-full max-w-2xl overflow-y-auto overscroll-contain px-4 pb-4 pt-3">
        {visibleScores.length > 0 ? (
          <GoalScoreCard data={visibleScores} />
        ) : (
          <div className="flex h-full min-h-[280px] flex-col items-center justify-center px-6 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-lg border border-white/10 bg-header_green/50 text-white/45">
              <FiInbox className="h-6 w-6" />
            </div>
            <p className="mt-4 max-w-xs text-sm leading-[1.8] text-white/65">
              {hasError ? t("sendingFail") : t("noData")}
            </p>
            <p className="mt-1 font-sans text-xs text-white/35">
              {moment(selectedDate).format("YYYY-MM-DD")}
            </p>
          </div>
        )}
      </main>
    </div>
  );
};

const HeaderButton = ({ label, children, ...props }) => (
  <button
    type="button"
    aria-label={label}
    className="flex h-10 w-10 items-center justify-center rounded-lg border border-white/10 bg-white/10 text-lg text-white/75 transition hover:bg-white/20 hover:text-white active:scale-95"
    {...props}
  >
    {children}
  </button>
);
